import { useContext } from 'react'
import { useQuery, QueryKey } from '@tanstack/react-query'
import { getAuth } from 'firebase/auth'
import ApiContext from '../../context/ApiContext'

export type User = {
	id: string
	uid: string
	phoneNumber: string
	firstName: string
	lastName: string
	email?: string
	trialStartedAt: Date | null
}

export const getUserQueryKey = (): QueryKey => ['user', 'me']

interface UseUserResult {
	user: User | null | undefined
	isLoading: boolean
	error: Error | null
	refetch: () => void
}

const useUser = (enabled: boolean = true): UseUserResult => {
	const apiContext = useContext(ApiContext)

	const {
		data: user,
		isLoading,
		error,
		refetch,
	} = useQuery({
		queryKey: getUserQueryKey(),
		queryFn: async (): Promise<User | null> => {
			const auth = getAuth()
			const idToken = await auth.currentUser?.getIdToken(true)
			const response = await fetch(`${apiContext.apiUrl}/users/me`, {
				headers: {
					Authorization: `Bearer ${idToken}`,
				},
			})

			if (response.status === 404) {
				return null // User doesn't exist - new user case
			}

			if (!response.ok) {
				throw new Error(await response.text())
			}

			return response.json()
		},
		enabled,
	})

	return { user, isLoading, error, refetch }
}

export default useUser
